import { Component, effect, OnInit, signal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MediaInputService } from '../services/media-input/media-input.service';
import { toSignal } from '@angular/core/rxjs-interop';

import {
  MatCard,
  MatCardActions,
  MatCardContent,
  MatCardFooter,
  MatCardHeader,
  MatCardTitle,
} from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { catchError, of, take, timeout } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { SnackbarService } from '../services/snackbar/snackbar.service';
import { GeolocationService } from '../services/geolocation/geolocation.service';
import { DialogService } from '../services/dialog/dialog.service';
import { TOO_FAR_REASONS } from '../examinationResult.model';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';

@Component({
  selector: 'app-media-input',
  imports: [
    MatProgressSpinnerModule,
    MatLabel,
    MatButton,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatIconModule,
    MatCardContent,
    MatCardActions,
    MatCardFooter,
    MatFormField,
    MatSelect,
    MatOption,
    FormsModule,
    TranslatePipe,
  ],
  templateUrl: './media-input.component.html',
  styleUrl: './media-input.component.scss',
})
export class MediaInputComponent implements OnInit {
  secondsAgo = signal(0);
  locationOverride = signal(false);
  autoForwardActive = signal(false);
  evaluatingPermissions = signal(true);

  currentPosition;

  constructor(
    protected mediaInputService: MediaInputService,
    private readonly snackbarService: SnackbarService,
    protected geolocationService: GeolocationService,
    private readonly dialogService: DialogService,
    private readonly router: Router,
  ) {
    this.currentPosition = toSignal(this.geolocationService.currentPosition$);

    effect((onCleanup) => {
      const pos = this.currentPosition();
      if (pos !== undefined) {
        this.secondsAgo.set(0);
        this.checkAutoForward();
        const id = globalThis.setInterval(() => {
          this.secondsAgo.update(v => v + 1);
        }, 1000);
        onCleanup(() => globalThis.clearInterval(id));
      }
    });
  }

  ngOnInit(): void {
    const isManual = globalThis.history.state?.manual === true;

    if (navigator.permissions?.query) {
      Promise.all([
        navigator.permissions.query({name: 'geolocation'}).catch(() => ({state: 'denied'} as PermissionStatus)), // NOSONAR
        navigator.permissions.query({name: 'camera' as PermissionName}).catch(() => ({state: 'denied'} as PermissionStatus))
      ]).then(([geoStatus, cameraStatus]) => {
        const geoGranted = geoStatus.state === 'granted';
        const cameraGranted = cameraStatus.state === 'granted';

        if (geoGranted) {
          this.geolocationService.triggerWatch();
        }
        if (cameraGranted) {
          this.requestVideoPermission(false).then(() => this.checkAutoForward());
        }

        if (!isManual && geoGranted && cameraGranted) {
          this.autoForwardActive.set(true);
          this.checkAutoForward();
        } else {
          this.evaluatingPermissions.set(false);
        }
      });
    } else {
      this.evaluatingPermissions.set(false);
    }
  }

  checkAutoForward() {
    if (this.autoForwardActive() && !this.isContinueButtonDisabled) {
      this.autoForwardActive.set(false);
      this.setConfigurationAndNavigate();
    }
  }

  requestGeolocationPermission() {
    this.geolocationService.triggerWatch();
    this.geolocationService.currentPosition$
      .pipe(
        take(1),
        timeout(10000),
        catchError(() => {
          if (environment.allow_start_without_gps) {
            this.dialogService.openSelectDialog(
              TOO_FAR_REASONS.map((reason) => {
                return {
                  option: reason,
                  label: 'enums.too_far_reason.' + reason,
                };
              }),
              'components.selected-lead-list.too-far-label',
              'components.media-input.location-card.location-override-text',
              () => {
                this.locationOverride.set(true);
              },
              'components.media-input.location-card.location-error',
            );
          }
          return of(undefined);
        }),
      )
      .subscribe();
  }

  async requestVideoPermission(forcePrompt = true) {
    try {
      if (forcePrompt) {
        const mediaStream = await navigator.mediaDevices.getUserMedia({video: true});
        this.mediaInputService.closeMediaStreamTracks(mediaStream);
      } else {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!devices.some(d => d.label && d.kind === 'videoinput')) {
          const mediaStream = await navigator.mediaDevices.getUserMedia({video: true});
          this.mediaInputService.closeMediaStreamTracks(mediaStream);
        }
      }

      const mediaDeviceInfo = await navigator.mediaDevices.enumerateDevices();
      this.mediaInputService.setMediaDeviceInfo(mediaDeviceInfo);
    } catch (error) {
      if (error instanceof Error && forcePrompt) {
        this.snackbarService.show(error.message);
      }
    }
  }

  get isContinueButtonDisabled(): boolean {
    return (
      this.mediaInputService.selectedVideoDevice === undefined ||
      (this.currentPosition() === undefined && !this.locationOverride())
    );
  }

  setConfigurationAndNavigate() {
    this.mediaInputService.setCurrentMediaConfiguration(
      this.mediaInputService.selectedVideoDevice!,
    );
    this.router.navigate(['/lead-pool']);
  }
}
